import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, Building2, FileText, ExternalLink, ChevronDown, FileSpreadsheet, FileDown, UploadCloud, Replace, Trash2, Loader2 } from "lucide-react";
import { fileUrl } from "../lib/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AdminSmmeEditor } from "../components/AdminSmmeEditor";
import { Pencil } from "lucide-react";

const selCls = "px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";

function MultiSelect({ label, options, selected, onToggle, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const summary = selected.length === 0 ? `All ${label}` : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 ${selCls} ${selected.length ? "border-[#00427A] text-[#00427A] font-medium" : "text-slate-600"}`}>
        <span className="truncate">{summary}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`${testid}-menu`} className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg py-1">
          {options.map((o) => {
            const checked = selected.includes(o);
            return (
              <label key={o} data-testid={`${testid}-option-${o}`}
                className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  className="h-4 w-4 rounded border-slate-300 text-[#00427A] focus:ring-[#00427A]/20" />
                <span className={checked ? "text-[#00427A] font-medium" : "text-slate-700"}>{o}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SmmeDatabase() {
  const { meta, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ company_name: "", districts: [], towns: [], sectors: [], bee_level: "" });
  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState("");
  const [expiryInputs, setExpiryInputs] = useState({});
  const [editing, setEditing] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "districts") { if (v.length) params.district = v.join(","); }
      else if (k === "towns") { if (v.length) params.town = v.join(","); }
      else if (k === "sectors") { if (v.length) params.sector = v.join(","); }
      else if (v) params[k] = v;
    });
    const { data } = await api.get("/smmes", { params });
    setRows(data);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleDistrict = (d) => {
    setFilters((f) => ({
      ...f,
      districts: f.districts.includes(d) ? f.districts.filter((x) => x !== d) : [...f.districts, d],
    }));
  };

  const toggleTown = (t) => {
    setFilters((f) => ({
      ...f,
      towns: f.towns.includes(t) ? f.towns.filter((x) => x !== t) : [...f.towns, t],
    }));
  };

  const toggleSector = (s) => {
    setFilters((f) => ({
      ...f,
      sectors: f.sectors.includes(s) ? f.sectors.filter((x) => x !== s) : [...f.sectors, s],
    }));
  };

  const openDetail = async (smme) => {
    setDetail(smme);
    setEditing(false);
    setExpiryInputs({});
    try {
      const { data } = await api.get(`/documents/user/${smme.user_id}`);
      setDocs(data);
    } catch { setDocs([]); }
  };

  const reloadDocs = async (userId) => {
    try {
      const { data } = await api.get(`/documents/user/${userId}`);
      setDocs(data);
    } catch { setDocs([]); }
  };

  const adminUpload = async (docType, file, hasExpiry) => {
    if (!file || !detail) return;
    setUploadingDoc(docType);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("target_user_id", detail.user_id);
    const expiry = expiryInputs[docType] || docs.find((d) => d.doc_type === docType)?.expiry_date;
    if (hasExpiry && expiry) fd.append("expiry_date", expiry);
    try {
      await api.post("/admin/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document saved");
      reloadDocs(detail.user_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingDoc("");
    }
  };

  const adminDeleteDoc = async (docType) => {
    if (!detail || !window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/admin/documents/${detail.user_id}/${docType}`);
      toast.success("Document removed");
      reloadDocs(detail.user_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteSmme = async (s) => {
    if (!window.confirm(`Delete ${s.company_name || s.email} permanently? This removes the account, profile, documents and responses.`)) return;
    try {
      await api.delete(`/admin/users/${s.user_id}`);
      toast.success("SMME deleted");
      setDetail(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCols = [
    ["Company", (s) => s.company_name || ""],
    ["Contact", (s) => `${s.name || ""} ${s.surname || ""}`.trim()],
    ["Email", (s) => s.email || ""],
    ["Phone", (s) => s.contact_number || ""],
    ["District", (s) => s.district || ""],
    ["Municipality", (s) => s.municipality || ""],
    ["Town", (s) => s.town || ""],
    ["Sector", (s) => s.sector || ""],
    ["BEE Level", (s) => s.bee_level || ""],
    ["Tax No.", (s) => s.tax_number || ""],
    ["Reg No.", (s) => s.registration_number || ""],
  ];
  const stamp = () => new Date().toISOString().slice(0, 10);

  const exportExcel = () => {
    const header = exportCols.map(([h]) => h);
    const data = rows.map((s) => exportCols.map(([, fn]) => fn(s)));
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    ws["!cols"] = header.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SMMEs");
    XLSX.writeFile(wb, `smme-database-${stamp()}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Assmang SMME Database", 14, 14);
    doc.setFontSize(9);
    doc.text(`Exported ${stamp()} · ${rows.length} supplier(s)`, 14, 20);
    autoTable(doc, {
      startY: 24,
      head: [exportCols.map(([h]) => h)],
      body: rows.map((s) => exportCols.map(([, fn]) => fn(s))),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [0, 66, 122] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    doc.save(`smme-database-${stamp()}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader eyebrow="Directory" title="SMME Database"
          subtitle="Search and filter all registered Northern Cape suppliers." />
        <div className="flex gap-2">
          <button data-testid="smme-export-excel" onClick={exportExcel} disabled={rows.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#1D6F42] text-white text-sm font-medium hover:bg-[#165a35] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button data-testid="smme-export-pdf" onClick={exportPdf} disabled={rows.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#B3261E] text-white text-sm font-medium hover:bg-[#8f1e18] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <FileDown className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="smme-filter-company" placeholder="Company name" value={filters.company_name}
              onChange={(e) => setFilters({ ...filters, company_name: e.target.value })}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
          </div>
          <select data-testid="smme-filter-bee-select" className={selCls} value={filters.bee_level} onChange={(e) => setFilters({ ...filters, bee_level: e.target.value })}>
            <option value="">All BEE Levels</option>
            {(meta?.bee_levels || []).map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Districts</p>
            <MultiSelect label="Districts" testid="smme-filter-district" options={meta?.districts || []}
              selected={filters.districts} onToggle={toggleDistrict} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Towns / Villages</p>
            <MultiSelect label="Towns" testid="smme-filter-town" options={meta?.towns || []}
              selected={filters.towns} onToggle={toggleTown} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sectors</p>
            <MultiSelect label="Sectors" testid="smme-filter-sector" options={meta?.sectors || []}
              selected={filters.sectors} onToggle={toggleSector} />
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty icon={Users} title="No SMMEs found" subtitle="Adjust your filters." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Company</th>
                <th className="px-5 py-3 font-semibold">District</th>
                <th className="px-5 py-3 font-semibold">Town</th>
                <th className="px-5 py-3 font-semibold">Sector</th>
                <th className="px-5 py-3 font-semibold">BEE</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s) => (
                <tr key={s.id} data-testid={`smme-row-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-4">
                    <p className="font-semibold text-slate-900">{s.company_name || "—"}</p>
                    <p className="text-xs text-slate-500">{s.name} {s.surname} · {s.email}</p>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{s.district || "—"}</td>
                  <td className="px-5 py-4 text-slate-600">{s.town || "—"}</td>
                  <td className="px-5 py-4 text-slate-600">{s.sector || "—"}</td>
                  <td className="px-5 py-4"><span className="font-mono-data text-xs font-semibold">{s.bee_level || "—"}</span></td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`smme-view-${s.id}`} onClick={() => openDetail(s)} className="text-[#00427A] font-semibold hover:underline">View</button>
                      {isAdmin && (
                        <>
                          <button data-testid={`smme-edit-${s.id}`} onClick={() => { openDetail(s); setEditing(true); }} className="text-slate-600 font-semibold hover:underline">Edit</button>
                          <button data-testid={`smme-delete-${s.id}`} onClick={() => deleteSmme(s)} className="text-red-600 font-semibold hover:underline">Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[#00427A]" /> {detail?.company_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              {isAdmin && !editing && (
                <div className="flex gap-2">
                  <button data-testid="detail-edit-btn" onClick={() => setEditing(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#00427A] text-[#00427A] text-sm font-semibold hover:bg-[#00427A]/5"><Pencil className="h-4 w-4" /> Edit Profile</button>
                  <button data-testid="detail-delete-btn" onClick={() => deleteSmme(detail)} className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50"><Trash2 className="h-4 w-4" /> Delete SMME</button>
                </div>
              )}
              {isAdmin && editing ? (
                <AdminSmmeEditor key={detail.user_id} smme={detail} meta={meta} onCancel={() => setEditing(false)}
                  onSaved={(p) => { setDetail({ ...detail, ...p }); setEditing(false); load(); }} />
              ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ["Contact", `${detail.name} ${detail.surname}`], ["Email", detail.email],
                  ["Phone", detail.contact_number], ["Tax No.", detail.tax_number],
                  ["Reg No.", detail.registration_number], ["BEE Level", detail.bee_level],
                  ["District", detail.district], ["Municipality", detail.municipality],
                  ["Town", detail.town], ["Sector", detail.sector],
                ].map(([k, v]) => (
                  <div key={k}><p className="text-xs text-slate-400 uppercase tracking-wide">{k}</p><p className="text-slate-800 font-medium">{v || "—"}</p></div>
                ))}
              </div>
              )}

              {isAdmin ? (
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-2">Manage Compliance Documents</p>
                  <div className="space-y-2">
                    {(meta?.doc_types || []).map((dt) => {
                      const existing = docs.find((d) => d.doc_type === dt.key);
                      return (
                        <div key={dt.key} data-testid={`admin-doc-${dt.key}`} className="border border-slate-200 rounded-md p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800">{dt.label}</p>
                              {existing ? (
                                <a href={fileUrl(existing.storage_path)} target="_blank" rel="noreferrer"
                                  className="text-xs text-[#00427A] hover:underline inline-flex items-center gap-1 truncate max-w-[220px]">
                                  <FileText className="h-3 w-3" /> {existing.original_filename}{existing.expiry_date ? ` · exp ${existing.expiry_date}` : ""}
                                </a>
                              ) : <p className="text-xs text-slate-400">Not uploaded</p>}
                            </div>
                            {existing && (
                              <button data-testid={`admin-doc-delete-${dt.key}`} onClick={() => adminDeleteDoc(dt.key)}
                                className="p-1.5 text-slate-400 hover:text-red-600 border border-slate-200 rounded-md"><Trash2 className="h-3.5 w-3.5" /></button>
                            )}
                          </div>
                          {dt.has_expiry && (
                            <input type="date" data-testid={`admin-doc-expiry-${dt.key}`}
                              defaultValue={existing?.expiry_date || ""}
                              onChange={(e) => setExpiryInputs({ ...expiryInputs, [dt.key]: e.target.value })}
                              className="mt-2 w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
                          )}
                          <label data-testid={`admin-doc-upload-${dt.key}-label`}
                            className="mt-2 inline-flex items-center justify-center gap-2 text-sm font-semibold cursor-pointer border border-[#00427A] text-[#00427A] hover:bg-[#00427A]/5 px-3 py-1.5 rounded-md transition-colors">
                            {uploadingDoc === dt.key ? <Loader2 className="h-4 w-4 animate-spin" /> : existing ? <Replace className="h-4 w-4" /> : <UploadCloud className="h-4 w-4" />}
                            {existing ? "Replace / Save" : "Upload"}
                            <input data-testid={`admin-doc-upload-${dt.key}-input`} type="file" className="hidden"
                              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                              onChange={(e) => adminUpload(dt.key, e.target.files[0], dt.has_expiry)} />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-2">Compliance Documents</p>
                  {docs.length === 0 ? <p className="text-sm text-slate-400">No documents uploaded.</p> : (
                    <div className="space-y-2">
                      {docs.map((d) => (
                        <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer"
                          className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2 text-sm hover:border-[#00427A]">
                          <span className="flex items-center gap-2 text-slate-700"><FileText className="h-4 w-4 text-slate-400" /> {d.doc_type} {d.expiry_date ? `· exp ${d.expiry_date}` : ""}</span>
                          <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
